import React, { useEffect, useState } from "react";
import {
  Checkbox,
  Page,
  Form,
  Card,
  InlineGrid,
  Text,
  InlineStack,
  Select,
  Bleed,
  Box,
  BlockStack,
  FormLayout,
  TextField,
  ResourceList,
  ResourceItem,
  LegacyStack,
  InlineError
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import "./validation-attempt-questionnaire-view.scss";
import { getAllCountryOptions, countryCodes, getAllCountryISOptions } from "constants/countries";

const EMPTY_FORM_DATA = {
  companyName: "",
  name: "",
  legal_name: "",
  street: "",
  city: "",
  region: "",
  country: "",
  countryCode: "",
  zipCodeLetters: "",
  zipCodeFigures: "",
  houseNumber: "",
  officeNumber: "",
  iban: "",
  iban2: "",
  accounts: [],
  vatCodeLetters: "",
  vatCodeFigures: "",
  phone: "",
  email: "",
  website: "",
  bankName: "",
  swiftCode: "",
  companyCode: "",
  contactFullName: "",
  contactPhone: "",
  contactPosition: "",
  contactEmail: "",
  signatureFullName: "",
  signaturePosition: "",
  signatureEmail: "",
  signatureBasisOfRepresentation: "",
  invoiceEmail: "",
  invoiceEmail2: "",
  paymentsFullName: "",
  paymentsPhone: "",
  paymentsPosition: "",
  paymentsEmail: "",
  signerPosition: "",
  signerFullName: "",
  signerFirstName: "",
  signerLastName: "",
  officeAddress: "",
  uploadedFiles: [],
  isFileUploadOptional: false,
  isVatPayer: null,
};

/**
 * Older attempts stored phone as countryCode+number (API shape).
 * Strip the dial prefix so the phone field matches what the customer typed.
 */
function phoneWithoutCountryCode(phone, countryCode) {
  if (!phone || !countryCode) return phone || "";
  const digitsPhone = String(phone).replace(/\D/g, "");
  const digitsCode = String(countryCode).replace(/\D/g, "");
  if (digitsCode && digitsPhone.startsWith(digitsCode)) {
    return digitsPhone.substring(digitsCode.length);
  }
  return String(phone);
}

/**
 * Use the saved attempt payload for display while restoring controlled-input defaults.
 */
function formDataFromAttemptPayload(payload) {
  const source = payload && typeof payload === "object" ? payload : {};
  const normalizedFields = Object.fromEntries(
    Object.entries(EMPTY_FORM_DATA).map(([key, defaultValue]) => [
      key,
      source[key] ?? defaultValue,
    ])
  );
  return {
    ...source,
    ...normalizedFields,
    // Form UI binds legal_name; submit payload may also include legalName
    legal_name: source.legal_name ?? source.legalName ?? "",
    phone: phoneWithoutCountryCode(source.phone, source.countryCode),
    uploadedFiles: Array.isArray(source.uploadedFiles)
      ? source.uploadedFiles
      : [],
  };
}

function ValidationAttemptQuestionnaireView({ attempt }) {
  const [showFormError, setShowFormError] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [validationErrors, setValidationErrors] = useState({});
  const [isAgreeDeliverElectronically, setIsAgreeDeliverElectronically] =
    useState(false);
  const [isDisagreeDeliverElectronically, setIsDisagreeDeliverElectronically] =
    useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    const payload = attempt?.payload || {};
    setFormData(formDataFromAttemptPayload(payload));
    setIsAgreeDeliverElectronically(
      Boolean(payload.isAgreeDeliverElectronically)
    );
    setIsDisagreeDeliverElectronically(
      Boolean(payload.isDisagreeDeliverElectronically)
    );
    const errors = attempt?.validationErrors || {};
    setValidationErrors(errors);
    setShowFormError(Object.keys(errors).length > 0);
  }, [attempt]);

  return (
    <>
      <Page fullWidth>
        <div className="section_heading_">
          <InlineStack align="center">
            <Text fontWeight="bold" variant="headingXl">
              {formData?.companyName
                ? `${formData.companyName.toUpperCase()} `
                : ""}
              PARTNERIO ANKETA / PARTNER PROFILE FORM
            </Text>
          </InlineStack>
        </div>
        <br />
        <div className="questionnaire-form questionnaire-form--readonly">
          <Form onSubmit={() => {}}>
            <FormLayout>
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <InlineGrid columns="1fr auto">
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        1.{t("customer_form-general_info")}
                      </Text>
                    </div>
                  </InlineGrid>
                  {/* first – label left, box right (horizontal layout) */}
                  <BlockStack gap="500">
                  {/* first – label left, box right; use LegacyStack so box aligns with Company code row */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        {t(" Teisinė forma ir pavadinimas / Legal form and Company name")}:
                      </LegacyStack.Item>
                      <div className="form-row-single-field min-w-0 w-full">
                        {/* Errors render below this bordered row (not inside), so they do not overlap the frame */}
                        <div className="legal-company-one-box min-h-8 w-full min-w-0">
                          <div className="legal-company-one-box__cell flex min-h-8 min-w-0 flex-col border-r border-[#c9c9c9] px-0 py-0">
                            <div className="legal-company-one-box__field-wrap flex min-h-8 min-w-0 flex-1 flex-col justify-start">
                              <TextField
                                align="left"
                                id="customer-form-legal-name"
                                variant="borderless"
                                label={t("legal_name")}
                                value={formData.legal_name}
                                onChange={() => {}}
                                requiredIndicator
                                error={Boolean(validationErrors.legal_name)}
                                labelHidden
                              />
                            </div>
                          </div>
                          <div className="legal-company-one-box__cell flex min-h-8 min-w-0 flex-col px-0 py-0">
                            <div className="legal-company-one-box__field-wrap flex min-h-8 min-w-0 flex-1 flex-col justify-start">
                              <TextField
                                align="left"
                                id="customer-form-name"
                                variant="borderless"
                                label={t("customer_form-name")}
                                value={formData.name}
                                onChange={() => {}}
                                requiredIndicator
                                error={Boolean(validationErrors.name)}
                                labelHidden
                              />
                            </div>
                          </div>
                        </div>
                        <div className="legal-company-one-box__errors mt-1 w-full min-w-0">
                          <div className="min-w-0 pr-2">
                            <InlineError
                              message={validationErrors.legal_name}
                              fieldID="customer-form-legal-name"
                            />
                          </div>
                          <div className="min-w-0 pl-2">
                            <InlineError
                              message={validationErrors.name}
                              fieldID="customer-form-name"
                            />
                          </div>
                        </div>
                        <div className="legal-company-one-box__hints mt-1 text-xs text-[#6d7175]">
                          <span className="min-w-0 flex-1 break-words pr-2">
                            {t("(pvz. UAB, AB, MB, IV, kt. / e.g., LLC, SIA, Sp. z o.o., etc.)")}
                          </span>
                          <span className="min-w-0 flex-1 break-words pl-2">
                            {t("(įmonės tikslus pavadinimas / Exact company name)")}
                          </span>
                        </div>
                      </div>
                    </LegacyStack>
                    {/* Company code */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        {t("Įmonės kodas/ Company code")}:
                      </LegacyStack.Item>
                      <div className="form-row-single-field">
                        <TextField
                          label={`${t("customer_form-companyCode")}`}
                          value={formData.companyCode}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.companyCode}
                          helpText={t("customer_form-companyCode_help")}
                          labelHidden
                        />
                      </div>
                    </LegacyStack>
                    {/* Is VAT payer? */}
                    <LegacyStack wrap={false}>
                      <LegacyStack.Item fill>
                        {t("Ar esate PVM mokėtojas? / Are you a VAT payer?")}:
                      </LegacyStack.Item>
                      <div className="form-row-single-field">
                        <LegacyStack gap="loose">
                          <Checkbox
                            label={t("Taip / Yes")}
                            checked={formData.isVatPayer === true}
                            onChange={() => {}}
                            disabled
                          />
                          <Checkbox
                            label={t("Ne / No")}
                            checked={formData.isVatPayer === false}
                            onChange={() => {}}
                            disabled
                          />
                        </LegacyStack>
                        {validationErrors.isVatPayer && (
                          <div style={{ marginTop: "4px" }}>
                            <InlineError
                              message={validationErrors.isVatPayer}
                              fieldID="isVatPayer"
                            />
                          </div>
                        )}
                      </div>
                    </LegacyStack>

                    {/* VAT code */}
                    {formData.isVatPayer && (
                      <LegacyStack wrap={false}>
                        <LegacyStack.Item fill>
                          {t("PVM kodas/VAT code")}:
                        </LegacyStack.Item>
                        <div className="form-row-two-fields">
                          <Select
                            label={t("customer_form-vatCode_country")}
                            options={getAllCountryISOptions(t)}
                            value={formData.vatCodeLetters}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.vatCodeLetters}
                            labelHidden
                            helpText={t("(Šalis / Country)")}
                          />
                          <TextField
                            label={t("customer_form-vatCode")}
                            value={formData.vatCodeFigures}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.vatCodeFigures}
                            autoComplete="off"
                            helpText={t("(Kodas / Code)")}
                            labelHidden
                          />
                        </div>
                      </LegacyStack>
                    )}

                  </BlockStack>
                  {/* second */}
                  <InlineGrid columns="1fr auto">
                    <Text as="h2" variant="bodyMd" fontWeight="semibold">
                      {t("customer_form-reg_address")}
                    </Text>
                  </InlineGrid>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t(
                          "Šalis (kur registruota)/ Country (of registration)"
                        )}
                        :
                      </LegacyStack.Item>

                      <Select
                        label={`${t("customer_form-country")}`}
                        options={getAllCountryOptions(t)}
                        onChange={() => {}}
                        value={formData.country}
                        requiredIndicator
                        error={validationErrors.country}
                        helpText={`${t("customer_form-country_help")}`}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Rajonas/ District")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("Rajonas/ District")}`}
                        value={formData.region}
                        onChange={() => {}}
                        error={validationErrors.region}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Miestas/ City")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("Miestas/ City")}`}
                        value={formData.city}
                        onChange={() => {}}
                        requiredIndicator
                        error={validationErrors.city}
                        helpText={t("customer_form-city_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Gatvė/ Street")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("Gatvė/ Street")}`}
                        value={formData.street}
                        onChange={() => {}}
                        requiredIndicator
                        error={validationErrors.street}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Namo numeris / House number")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("Namo numeris / House number")}`}
                        value={formData.houseNumber}
                        onChange={() => {}}
                        error={validationErrors.houseNumber}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Ofiso numeris / Office number (if applicable)")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t(
                          "Ofiso numeris / Office number (if applicable)"
                        )}`}
                        value={formData.officeNumber}
                        onChange={() => {}}
                        error={validationErrors.officeNumber}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Pašto kodas/ Postal code")}:
                      </LegacyStack.Item>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "8px",
                        }}
                      >
                        <TextField
                          label={`Raidės / Letters`}
                          value={formData.zipCodeLetters}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.zipCodeLetters}
                          autoComplete="off"
                          helpText={t("(Raidės / Letters)")}
                          labelHidden
                        />
                        <TextField
                          label={`Skaičiai / Figures`}
                          value={formData.zipCodeFigures}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.zipCodeFigures}
                          autoComplete="off"
                          helpText={t("(Skaičiai / Figures)")}
                          labelHidden
                        />
                      </div>
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t(
                          "Faktinis (veiklos) adresas/ Actual business address"
                        )}
                        :
                      </LegacyStack.Item>

                      <TextField
                        label={`${t(
                          "Faktinis (veiklos) adresas/ Actual business address"
                        )}`}
                        value={formData.officeAddress}
                        onChange={() => {}}
                        error={validationErrors.officeAddress}
                        helpText={t(
                          "(nurodoma jeigu skiriasi adresas/ to be provided if it differs from the registered address)"
                        )}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Tel. Nr./ Phone number")}:
                      </LegacyStack.Item>

                      <div
                        style={{ display: "flex", gap: "8px", width: "100%" }}
                      >
                        <div className="w-35">
                          <Select
                            label={`Šalies kodas / Country Code`}
                            options={Object.entries(countryCodes).map(
                              ([iso, dial]) => ({
                                value: dial,
                                label: `${iso} (${dial})`,
                              })
                            )}
                            value={formData.countryCode}
                            onChange={() => {}}
                            //helpText={t("Šalies kodas / Country Code")}
                            labelHidden
                          />
                        </div>
                        <div className="w-65">
                          <TextField
                            label={`Telefono numeris / Phone Number`}
                            value={formData.phone}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.phone}
                            autoComplete="off"
                            helpText={t("(Telefono numeris / Phone Number)")}
                            labelHidden
                          />
                        </div>
                      </div>
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("El. paštas/ E-mail")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("El. paštas/ E-mail")}`}
                        value={formData.email}
                        onChange={() => {}}
                        requiredIndicator
                        error={validationErrors.email}
                        helpText={t("customer_form-email_help")}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-website")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("customer_form-website")}`}
                        value={formData.website}
                        error={validationErrors.website}
                        onChange={() => {}}
                        helpText={t("customer_form-website_help")}
                        labelHidden
                      />
                    </LegacyStack>
                  </BlockStack>
                </BlockStack>
              </Card>

              {/* third */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        2.
                        {t("Banko duomenys / Bank details")}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-iban")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("customer_form-iban")}`}
                        value={formData.iban}
                        onChange={() => {}}
                        error={validationErrors.iban}
                        helpText={t("customer_form-iban_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t(
                          "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                        )}
                      </LegacyStack.Item>
                      <TextField
                        label={`${t(
                          "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                        )}`}
                        value={formData.iban2}
                        onChange={() => {}}
                        error={validationErrors.iban2}
                        helpText={t(
                          "(pildoma tik tuo atveju, jeigu nėra IBAN/ to be completed only if no IBAN is available)"
                        )}
                        placeholder={t("customer_form-iban2_placeholder")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-bankName")}:
                      </LegacyStack.Item>

                      <TextField
                        label={`${t("customer_form-bankName")}`}
                        value={formData.bankName}
                        onChange={() => {}}
                        requiredIndicator
                        error={validationErrors.bankName}
                        helpText={t("customer_form-bankName_help")}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-swiftCode")}:
                      </LegacyStack.Item>
                      <TextField
                        label={`${t("customer_form-swiftCode")}`}
                        value={formData.swiftCode}
                        onChange={() => {}}
                        error={validationErrors.swiftCode}
                        helpText={t("customer_form-swiftCode_help")}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-currency")}:
                      </LegacyStack.Item>
                      <TextField
                        label={`${t("customer_form-currency")}`}
                        value={formData.accounts?.[0]?.currency || ""}
                        onChange={() => {}}
                        error={validationErrors.currency}
                        labelHidden
                      />
                    </LegacyStack>
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("customer_form-account_comment")}:
                      </LegacyStack.Item>
                      <TextField
                        label={`${t("customer_form-account_comment")}`}
                        value={formData.accounts?.[0]?.comment || ""}
                        onChange={() => {}}
                        error={validationErrors.comment}
                        labelHidden
                      />
                    </LegacyStack>

                    {/* The 2nd, 3rd… account the partner had typed when the
                        submit was blocked — same order as in the form, with the
                        errors that blocked it. */}
                    {(formData.accounts || [])
                      .slice(1)
                      .map((account, index) => (
                        <BlockStack gap="400" key={`account-${index}`}>
                          <Text as="h3" variant="headingSm">
                            {t("customer_form-account_heading", {
                              number: index + 2,
                            })}
                          </Text>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t("customer_form-iban")}:
                            </LegacyStack.Item>
                            <TextField
                              label={`${t("customer_form-iban")}`}
                              value={account.iban || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_iban`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t(
                                "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                              )}
                            </LegacyStack.Item>
                            <TextField
                              label={`${t(
                                "*Kitas ne IBAN Nr./ Alternative account number (if not IBAN):"
                              )}`}
                              value={account.iban2 || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_iban2`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t("customer_form-bankName")}:
                            </LegacyStack.Item>
                            <TextField
                              label={`${t("customer_form-bankName")}`}
                              value={account.bankName || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_bankName`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t("customer_form-swiftCode")}:
                            </LegacyStack.Item>
                            <TextField
                              label={`${t("customer_form-swiftCode")}`}
                              value={account.swiftCode || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_swiftCode`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t("customer_form-currency")}:
                            </LegacyStack.Item>
                            <TextField
                              label={`${t("customer_form-currency")}`}
                              value={account.currency || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_currency`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                          <LegacyStack>
                            <LegacyStack.Item fill>
                              {t("customer_form-account_comment")}:
                            </LegacyStack.Item>
                            <TextField
                              label={`${t("customer_form-account_comment")}`}
                              value={account.comment || ""}
                              onChange={() => {}}
                              error={
                                validationErrors[
                                  `extraAccount_${index}_comment`
                                ]
                              }
                              labelHidden
                            />
                          </LegacyStack>
                        </BlockStack>
                      ))}
                  </BlockStack>
                </BlockStack>
              </Card>

              {/* fourth */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <>
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        3.{`${t("customer_form-contacts")}`}
                      </Text>
                    </div>

                    <BlockStack gap="500">
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          {t("Kontaktinis asmuo/ Contact Person")}:
                        </LegacyStack.Item>

                        <TextField
                          label={`${t("Kontaktinis asmuo/ Contact Person")}`}
                          value={formData.contactFullName}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.contactFullName}
                          helpText={t("(vardas, pavardė/ first and last name)")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          {t("Tel. Nr./ Phone number")}:
                        </LegacyStack.Item>
                        <TextField
                          label={`${t("Tel. Nr./ Phone number")}`}
                          value={formData.contactPhone}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.contactPhone}
                          helpText={t("customer_form-contact_phone_help")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          {t("Pareigos/ Position")}:
                        </LegacyStack.Item>

                        <TextField
                          label={`${t("Pareigos/ Position:")}`}
                          value={formData.contactPosition}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.contactPosition}
                          helpText={t("(pareigos / position)")}
                          labelHidden
                        />
                      </LegacyStack>
                      <LegacyStack>
                        <LegacyStack.Item fill>
                          {t("customer_form-contact_email")}:
                        </LegacyStack.Item>

                        <TextField
                          label={`${t("customer_form-contact_email")}`}
                          type="email"
                          value={formData.contactEmail}
                          onChange={() => {}}
                          requiredIndicator
                          error={validationErrors.contactEmail}
                          helpText={t("customer_form-contact_email_help")}
                          labelHidden
                        />
                      </LegacyStack>
                    </BlockStack>
                  </>
                  <InlineGrid columns="1fr auto">
                    <Text as="h3" variant="headingSm">
                      {`${t("Vadovo duomenys/ Manager information")}`}
                    </Text>
                  </InlineGrid>
                  <Bleed marginInline="400">
                    <Box
                      background="bg-surface-secondary"
                      // paddingBlock="200"
                      paddingInline="400"
                    >
                      <BlockStack gap="500">
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            {t(
                              "Asmens kuris pasirašys sutartį/ Person authorized to execute the Contract"
                            )}
                            :
                          </LegacyStack.Item>
                          <TextField
                            label={`${t("customer_form-signature_fullName")}`}
                            value={formData.signatureFullName}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.signatureFullName}
                            helpText={t(
                              "(vardas, pavardė / first and last name)"
                            )}
                            labelHidden
                          />
                        </LegacyStack>
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            {t("Pareigos / Position")}:
                          </LegacyStack.Item>

                          <TextField
                            label={`${t("Pareigos / Position")}`}
                            value={formData.signaturePosition}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.signaturePosition}
                            helpText={t("(pareigos / position)")}
                            labelHidden
                          />
                        </LegacyStack>

                        <LegacyStack>
                          <LegacyStack.Item fill>
                            {t("El. paštas/ E-mail")}:
                          </LegacyStack.Item>
                          <TextField
                            label={`${t("El. paštas/ E-mail:")}`}
                            type="email"
                            value={formData.signatureEmail}
                            onChange={() => {}}
                            requiredIndicator
                            error={validationErrors.signatureEmail}
                            labelHidden
                          />
                        </LegacyStack>

                        <LegacyStack>
                          <LegacyStack.Item fill>
                            {t(
                              "Atstovavimo pagrindas/ Grounds for Representation"
                            )}
                            :
                          </LegacyStack.Item>
                          <TextField
                            label={`$`}
                            value={formData.signatureBasisOfRepresentation}
                            onChange={() => {}}
                            requiredIndicator
                            error={
                              validationErrors.signatureBasisOfRepresentation
                            }
                            labelHidden
                          />
                          <div
                            className="input-wid_help_text"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {t(
                              "customer_form-signature_basis_of_representation_help"
                            )}
                          </div>
                        </LegacyStack>
                      </BlockStack>
                    </Box>
                  </Bleed>
                  <br />
                </BlockStack>
              </Card>
              {/* fifth */}
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        4.
                        {`${t("Elektroninės sąskaitos / Electronic Invoices")}`}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="500">
                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t(
                          "El. paštas sąskaitų siuntimui/ E-mail for invoice submission"
                        )}
                        :
                      </LegacyStack.Item>
                      <TextField
                        label={`${t(
                          "El. paštas sąskaitų siuntimui/ E-mail for invoice submission"
                        )}`}
                        value={formData.invoiceEmail}
                        onChange={() => {}}
                        helpText={t("customer_form-invoice_email_help")}
                        requiredIndicator
                        error={validationErrors.invoiceEmail}
                        labelHidden
                      />
                    </LegacyStack>

                    <LegacyStack>
                      <LegacyStack.Item fill>
                        {t("Papildomas el. paštas / Secondary E-mail")}:
                      </LegacyStack.Item>
                      <TextField
                        label={`${t(
                          "Papildomas el. paštas / Secondary E-mail"
                        )}`}
                        value={formData.invoiceEmail2}
                        onChange={() => {}}
                        helpText={t(
                          "(pildoma tik tuo atveju, jeigu yra poreikis/to be completed only if necessary)"
                        )}
                        error={validationErrors.invoiceEmail2}
                        labelHidden
                      />
                    </LegacyStack>
                  </BlockStack>
                  <Checkbox
                    label={
                      <>
                        <strong>Sutinku</strong>, kad sutartys, sąskaitos ir jų
                        priedai būtų siunčiami tik elektroniniu būdu nurodytais
                        elektroninio pašto adresais/ <strong>I agree</strong>{" "}
                        that all contracts, invoices, and related documents
                        shall be delivered only electronically to the designated
                        email addresses.
                      </>
                    }
                    checked={isAgreeDeliverElectronically}
                    disabled
                  ></Checkbox>
                  <Checkbox
                    label={
                      <>
                        <strong>Nesutinku</strong>, kad dokumentai būtų
                        siunčiami tik elektroniniu būdu ir{" "}
                        <strong>sutinku</strong> sumokėti visas dokumentų
                        siuntimo ir parengimo siuntimui išlaidas/
                        <strong>I disagree</strong> with exclusive electronic
                        delivery of documents and <strong>agree</strong> to
                        cover all charges for document preparation and shipment.
                      </>
                    }
                    checked={isDisagreeDeliverElectronically}
                    disabled
                  />
                  {validationErrors.deliveryError && (
                    <p style={{ color: "red", marginTop: 4 }}>
                      Prašome pasirinkti tik vieną variantą / Please select only
                      one option.
                    </p>
                  )}
                </BlockStack>
              </Card>
              <Card roundedAbove="sm">
                <BlockStack gap="200">
                  <InlineGrid columns="1fr auto">
                    <div className="section_heading_">
                      <Text as="h2" variant="headingSm">
                        {`${t("customer_form-documents")}`}
                      </Text>
                    </div>
                    
                    
                  </InlineGrid>
                  
                    <Box
                      borderWidth="025"
                      borderColor="border"
                      padding="400"
                      background="bg-surface-secondary"
                      className="spec-section"
                    >
                      <BlockStack gap="200">
                        <Text as="p" fontWeight="bold">
                          Reikalingi pateikti dokumentai / Required documents to
                          be submitted:
                        </Text>
                        <BlockStack gap="100">
                          <Text as="p">
                            1. Įmonės registracijos dokumentų išrašo kopiją / A
                            copy of the Company's Certificate of Registration;
                          </Text>
                          <Text as="p">
                            2. Įmonės įgaliojimas, jeigu veikiama bus su
                            įgaliojimu / A Power of Attorney, if acting on
                            behalf of the Company under authorisation
                          </Text>
                        </BlockStack>
                      </BlockStack>
                    </Box>
                  <Text
                    as="p"
                    tone={validationErrors.documents ? "critical" : ""}
                  >
                    {formData.isFileUploadOptional
                      ? ""
                      : "Reikalingi pateikti dokumentai / Required documents to be submitted."}
                  </Text>
                  <ResourceList
                    resourceName={{
                      singular: t("customer_form-document"),
                      plural: t("customer_form-documents"),
                    }}
                    items={formData.uploadedFiles}
                    renderItem={(item) => {
                      const { id, filename } = item;

                      return (
                        <ResourceItem id={id}>
                          <InlineGrid columns="1fr auto">
                            <Text variant="bodyMd" fontWeight="bold" as="h3">
                              {filename}
                            </Text>
                          </InlineGrid>
                        </ResourceItem>
                      );
                    }}
                  />
                  </BlockStack>
                </Card>
              <Card roundedAbove="sm" className="customer_form-signature_block">
                <BlockStack gap="200">
                  <div className="section_heading_">
                    <InlineGrid columns="1fr auto">
                      <Text as="h2" variant="headingSm">
                        5.{t("Patvirtinimai/ Declarations")}
                      </Text>
                    </InlineGrid>
                  </div>
                  <BlockStack gap="200">
                    {/* Row 1 */}
                    <div className="spec-section" style={{ padding: '12px 16px', borderRadius: 4 }}>
                    <div>
                      <strong>Patvirtinu,</strong> kad Bendrovei ir tiesiogiai
                      ar netiesiogiai jį/ją valdančiam (-tiems)
                      (kontroliuojančiam (-tiems)) asmenims ir/ar jų vykdomos
                      veiklos/tiekiamų produktų atžvilgiu, nėra taikomos
                      tarptautinės (tame tarpe ir Jungtinių Amerikos Valstijų)
                      sankcijos ar kitos Europos Sąjungos (toliau – ES)
                      nustatytos ribojamosios priemonės/{" "}
                      <strong>I hereby declare</strong> that neither the
                      Company, nor any persons directly or indirectly
                      controlling or exercising control over the Company and/or
                      their respective activities or supplied products, are
                      subject to any international sanctions (including those
                      imposed by the United States) or other restrictive
                      measures imposed by the European Union (hereinafter
                      referred to as the “EU”).
                    </div>

                    {/* Row 2 */}
                    <div>
                      <strong>Man žinoma,</strong>
                      kad jeigu ši deklaracija yra neteisinga, melaginga ir (ar)
                      klaidinanti, su Bendrove sudaryta Sutartis gali būti
                      nutraukta ir Bendrovė privalės atlyginti dėl šios
                      melagingos deklaracijos pateikimo patirtus nukentėjusios
                      šalies nuostolius/ I acknowledge that if this declaration
                      is found to be false, misleading, or inaccurate, the
                      Contract concluded with the Company may be terminated, and
                      the Company shall be liable to indemnify the injured Party
                      for any losses or damages incurred as a result of such
                      false declaration.
                    </div>
                    {/* Row 3 */}
                    <div>
                      <strong>Paaiškėjus</strong>
                      apie Bendrovei tiesiogiai ar netiesiogiai jį valdančiam
                      (-tiems) (kontroliuojančiam (-tiems)) asmeniui (-ims),
                      ir/ar jų vykdomos veiklos/tiekiamų produktų atžvilgiu,
                      taikomas tarptautines (tame tarpe ir Jungtinių Amerikos
                      Valstijų) sankcijas ar kitas ES nustatytas ribojamąsias
                      priemones, įsipareigoju nedelsiant apie tai Jus
                      informuoti/ Should it become known that the Company, any
                      persons directly or indirectly controlling it (or
                      exercising control over it) and/or their respective
                      activities or supplied products, are subject to
                      international sanctions (including those imposed by the
                      United States) or other restrictive measures imposed by
                      the European Union, I undertake to immediately notify you
                      thereof.
                    </div>
                    </div>
                  </BlockStack>

                  <Bleed marginInline="400">
                    <Box
                      background="bg-surface-secondary"
                      paddingBlock="300"
                      paddingInline="400"
                      // className="atstovas-block"
                    >
                      <BlockStack gap="400">
                        <LegacyStack>
                          <LegacyStack.Item fill>
                            {t("Atstovas/ Representative")}:
                          </LegacyStack.Item>
                          <div className="representative-fields">
                            <TextField
                              label={`${t("customer_form-signer-signerFullName")}`}
                              value={formData.signerFullName}
                              onChange={() => {}}
                              autoComplete="off"
                              requiredIndicator
                              error={validationErrors.signerFullName}
                              helpText={`${t(
                                "(Vardas, pavardė/ First and last name)"
                              )}`}
                              labelHidden
                            />
                            <TextField
                              label={`${t("customer_form-signer-position")}`}
                              value={formData.signerPosition}
                              onChange={() => {}}
                              autoComplete="off"
                              requiredIndicator
                              error={validationErrors.signerPosition}
                              helpText={`${t("(Pareigos / Position)")}`}
                              labelHidden
                            />
                          </div>
                        </LegacyStack>

                        <BlockStack gap="200">
                          <Text
                            as="p"
                            variant="bodySm"
                            tone="subdued"
                            className="confirm-disclaimer"
                          >
                            {t("customer_form-confirm_disclaimer")}
                          </Text>
                          
                        </BlockStack>
                      </BlockStack>
                      {showFormError && (
                        <InlineStack align="end" gap="200">
                          <Text tone="critical">
                            {`${t("customer_form-submit_error")}`}
                          </Text>
                        </InlineStack>
                      )}
                    </Box>
                  </Bleed>
                </BlockStack>
              </Card>
            </FormLayout>
          </Form>
        </div>
      </Page>
    </>
  );
}

export default ValidationAttemptQuestionnaireView;
